import React, { useState } from 'react';
import { analysisApi, promptApi } from '../services/api';
import { generateAnalysisReport } from '../utils/pdfExportUtils';
import { generateAnalysisReportV2 } from '../utils/pdfExportUtilsV2';
import { filterFetchFailures } from '../utils/fetchFailureFilter';
import { BrandSafetyPrompt, AnalysisResult } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { Modal } from './ui/modal';
import { Button } from './ui/button';

interface PromptExportProps {
  promptId: string;
  promptName: string;
  prompt: BrandSafetyPrompt | null;
  totalUrls: number;
  matchedUrls: number;
  unmatchedUrls: number;
  averageConfidence: number;
  dailyAvails?: number;
  className?: string;
  /**
   * 'buttons' (default) — the original standalone v1/v2 button pair.
   * 'menu-items' — rows for the consolidated Export dropdown (deepcontext-c7ac).
   */
  renderAs?: 'buttons' | 'menu-items';
  /** Called when an export is kicked off from inside a dropdown, so the parent can close the menu. */
  onMenuAction?: () => void;
}

const PromptExport: React.FC<PromptExportProps> = ({
  promptId,
  promptName,
  prompt,
  totalUrls,
  matchedUrls,
  unmatchedUrls,
  averageConfidence,
  className = '',
  renderAs = 'buttons',
  onMenuAction,
}) => {
  const { user } = useAuth();
  const { selectedCompanyId } = useCompany();
  const scopeCompanyId = (user?.role === 'eskimi_admin' || user?.role === 'adops')
    ? selectedCompanyId || undefined
    : undefined;
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingV2, setIsExportingV2] = useState(false);
  const [exportVariant, setExportVariant] = useState<'v1' | 'v2'>('v1');
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [sampleCount, setSampleCount] = useState(8);

  const handleExportClick = () => {
    onMenuAction?.();
    setExportVariant('v1');
    setShowDialog(true);
    setError(null);
  };

  const handleExportV2Click = () => {
    onMenuAction?.();
    setExportVariant('v2');
    setShowDialog(true);
    setError(null);
  };

  const handleExport = async () => {
    setShowDialog(false);
    if (exportVariant === 'v2') setIsExportingV2(true); else setIsExporting(true);
    setError(null);

    try {
      // Over-fetch (3x) so that after dropping fetch-failed rows we can still fill
      // the requested sample count with clean samples.
      const overFetchCount = sampleCount * 3;
      const [matchedResponse, unmatchedResponse] = await Promise.all([
        analysisApi.getHistory(1, overFetchCount, { promptId, isBrandSafe: true }, undefined, scopeCompanyId),
        analysisApi.getHistory(1, overFetchCount, { promptId, isBrandSafe: false }, undefined, scopeCompanyId)
      ]);

      // Drop unfetchable / fetch-failed rows (no dedicated flag exists — text heuristic),
      // then trim back to the requested sample count.
      const matchedSampleRows = filterFetchFailures(matchedResponse.results).slice(0, sampleCount);
      const unmatchedSampleRows = filterFetchFailures(unmatchedResponse.results).slice(0, sampleCount);

      // Always fetch full prompt (with body) via getById; list API omits prompt_text for performance
      let promptDetails: BrandSafetyPrompt;
      try {
        promptDetails = selectedCompanyId
          ? await promptApi.getById(promptId, selectedCompanyId)
          : await promptApi.getById(promptId);
      } catch (err) {
        console.warn('Failed to fetch prompt details:', err);
        promptDetails = (prompt ?? {
          id: promptId,
          name: promptName,
          description: '',
          prompt: '',
          type: 'brand-safety' as const,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }) as BrandSafetyPrompt;
      }

      // Generate PDF with aggregated data
      const reportData = {
        bulkJob: {
          id: `prompt-${promptId}`,
          promptId: promptId,
          promptName: promptName,
          createdAt: new Date().toISOString(),
          totalUrls: totalUrls,
          matchedUrls: matchedUrls,
          unmatchedUrls: unmatchedUrls,
          averageConfidence: averageConfidence,
          totalCost: 0 // Not applicable for prompt-level export
        },
        prompt: {
          name: promptDetails.name,
          description: promptDetails.description,
          prompt: promptDetails.prompt,
          type: promptDetails.type
        },
        matchedSamples: matchedSampleRows.map((sample: AnalysisResult) => ({
          url: sample.url,
          reasoning: sample.reasoning,
          confidence: sample.confidence,
          contentSummary: sample.contentSummary
        })),
        unmatchedSamples: unmatchedSampleRows.map((sample: AnalysisResult) => ({
          url: sample.url,
          reasoning: sample.reasoning,
          confidence: sample.confidence,
          contentSummary: sample.contentSummary
        }))
      };

      if (exportVariant === 'v2') {
        await generateAnalysisReportV2(reportData);
      } else {
        await generateAnalysisReport(reportData);
      }

    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
      setIsExportingV2(false);
    }
  };

  const sampleDialog = (
    <Modal open={showDialog} onClose={() => setShowDialog(false)} title="Export PDF Report">
      <div className="mb-4">
        <label htmlFor="sampleCount" className="block text-sm font-medium text-ink mb-2">
          Number of URL samples to include (per category)
        </label>
        <input
          type="number"
          id="sampleCount"
          min="8"
          max="30"
          value={sampleCount}
          onChange={(e) => setSampleCount(Math.min(30, Math.max(8, parseInt(e.target.value) || 8)))}
          className="w-full px-3 py-2 border border-line rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
        />
        <p className="mt-1 text-sm text-ink-muted">
          Between 8 and 30 samples
        </p>
      </div>

      <div className="flex justify-end space-x-3">
        <Button type="button" variant="surface" size="md" onClick={() => setShowDialog(false)}>
          Cancel
        </Button>
        <Button type="button" variant="secondary" size="md" onClick={handleExport}>
          Export
        </Button>
      </div>
    </Modal>
  );

  if (renderAs === 'menu-items') {
    const busy = isExporting || isExportingV2;
    return (
      <div className={className}>
        <div className="px-4 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-ink-muted">Blueprint Report</div>
        <button
          onClick={handleExportClick}
          disabled={busy || totalUrls === 0}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-ink hover:bg-ghost-bg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Matched Report (PDF)
        </button>
        <button
          onClick={handleExportV2Click}
          disabled={busy || totalUrls === 0}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left text-ink hover:bg-ghost-bg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Sample Report (PDF v2)
        </button>
        {error && <div className="px-4 py-1 text-xs text-risk">{error}</div>}
        {sampleDialog}
      </div>
    );
  }

  return (
    <>
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <Button
          type="button"
          variant="surface"
          size="md"
          onClick={handleExportClick}
          disabled={isExporting || isExportingV2 || totalUrls === 0}
          title={totalUrls === 0 ? "No data to export" : "Export prompt overview as PDF"}
        >
          {isExporting ? (
            <>
              <svg className="animate-spin h-4 w-4 text-ink" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export PDF
            </>
          )}
        </Button>

        <button
          onClick={handleExportV2Click}
          disabled={isExporting || isExportingV2 || totalUrls === 0}
          // deepcontext-hkt0.5 — h-[42px] (rather than py-2) pins this
          // hand-rolled button to the app's 42px row-control standard, so it
          // sits flush with the shared Button `md` CTA beside it.
          className="inline-flex h-[42px] items-center px-3 border border-accent/40 shadow-sm text-sm leading-4 font-medium rounded-md text-accent-active bg-accent/10 hover:bg-accent/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
          title={totalUrls === 0 ? "No data to export" : "Export prompt overview as PDF (V2 — Brand-Forward layout, beta)"}
        >
          {isExportingV2 ? (
            <>
              <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-accent-active" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export PDF 2
            </>
          )}
        </button>
      </div>

      {sampleDialog}

      {error && (
        <div className="mt-2 text-sm text-risk">
          {error}
        </div>
      )}
    </>
  );
};

export default PromptExport;